import React, { Fragment, useMemo } from "react";

import Decimal from "decimal.js";
import { identity, orderBy, truncate } from "lodash";

import usePhysicalAccount from "@fartherfinance/frontend/api/Accounts/hooks/usePhysicalAccount";
import useVirtualAccount from "@fartherfinance/frontend/api/Accounts/hooks/useVirtualAccount";
import useClientProfile from "@fartherfinance/frontend/api/Entity/hooks/useClientProfile";
import { TradeProposalId } from "@fartherfinance/frontend/api/Investment/types";
import {
  ClientId,
  FartherAccountId,
  PhysicalAccountId,
} from "@fartherfinance/frontend/api/Types";

import { useIsMutualFunds } from "../useSecuritiesByTradeProposal.hooks";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { UNKNOWN_VALUE } from "@src/multiCustodian/pages/Advisor/common";
import Card from "@src/sharedComponents/Card/Card";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./ProposalDetails.module.css";

interface Props {
  accountIds: FartherAccountId[] | undefined; // undefined means loading
  tradeProposalId: TradeProposalId;
}

const CardAccount: React.FC<Props> = (props) => {
  const orderedAccounts = useMemo(() => {
    if (props.accountIds === undefined) {
      return undefined;
    }

    return orderBy(props.accountIds, [identity], ["asc"]);
  }, [props.accountIds]);

  const hasSmartAssetAllocation =
    props.accountIds === undefined ? undefined : props.accountIds.length > 1;

  return (
    <Card header="Farther Account">
      {(orderedAccounts ?? [undefined]).map((accountId, idx) => (
        <Fragment key={`${accountId ?? ""}-${idx}`}>
          {idx > 0 && <div className={styles.Divider} />}
          <AccountSection
            accountId={accountId}
            hasSmartAssetAllocation={hasSmartAssetAllocation}
            tradeProposalId={props.tradeProposalId}
          />
        </Fragment>
      ))}
    </Card>
  );
};

export default CardAccount;

interface AccountSectionProps {
  tradeProposalId: TradeProposalId;
  accountId: FartherAccountId | undefined;
  hasSmartAssetAllocation: boolean | undefined;
}

const AccountSection: React.FC<AccountSectionProps> = (props) => {
  const auth = useAdvisorRequestAuth();

  const virtualAccount = useVirtualAccount(props.accountId ?? null, auth);

  return (
    <>
      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Account</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {virtualAccount.data === undefined ? (
            <Skeleton width={100} />
          ) : (
            truncate(virtualAccount.data.displayName, { length: 25 })
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Farther Id</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {virtualAccount.data === undefined ? (
            <Skeleton width={100} />
          ) : (
            <Tooltip
              tooltipText={virtualAccount.data.virtualAccountId}
              placement="top"
            >
              <Text variant="body">
                {truncate(virtualAccount.data.virtualAccountId, { length: 24 })}
              </Text>
            </Tooltip>
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Account Type</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {virtualAccount.data?.accountType ?? <Skeleton width={100} />}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Asset location</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {props.hasSmartAssetAllocation === undefined ? (
            <Skeleton width={100} />
          ) : props.hasSmartAssetAllocation ? (
            "Yes"
          ) : (
            "No"
          )}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Account State</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {virtualAccount.data?.operationalState ?? <Skeleton width={100} />}
        </Text>
      </div>

      {(virtualAccount.data?.physicalAccountIds ?? [undefined]).map(
        (physicalAccountId, idx) => (
          <Fragment key={`${physicalAccountId ?? ""}-${idx}`}>
            {idx > 0 && <div className={styles.Divider} />}
            <PhysicalAccountSection
              accountId={physicalAccountId}
              tradeProposalId={props.tradeProposalId}
            />
          </Fragment>
        )
      )}

      <div className={styles.VerticalSeparator} />

      {virtualAccount.data?.accountHolders.map((accountHolder) => (
        <AccountHolder
          key={accountHolder.clientId}
          clientId={accountHolder.clientId}
          ownership={accountHolder.ownershipPercentage}
        />
      ))}
    </>
  );
};

interface AccountHolderProps {
  clientId: ClientId;
  ownership: Decimal;
}

const AccountHolder: React.FC<AccountHolderProps> = (props) => {
  const auth = useAdvisorRequestAuth();

  const clientAuth = useMemo(
    () =>
      auth === null || props.clientId === undefined
        ? null
        : { ...auth, clientId: props.clientId },
    [auth, props.clientId]
  );
  const clientProfile = useClientProfile(props.clientId, clientAuth);

  const fullName =
    clientProfile.data === undefined
      ? undefined
      : `${clientProfile.data.investorProfile.personalDetails.name.first} ${clientProfile.data.investorProfile.personalDetails.name.last}`;

  return (
    <div className={styles.FlexRow}>
      <Text variant="bodyBold">{fullName ?? <Skeleton width={100} />}</Text>
      <Text variant="bodyBold">:&nbsp;</Text>
      <Text variant="body">
        {props.ownership
          .toNumber()
          .toLocaleString("en-US", { style: "percent" })}
      </Text>
    </div>
  );
};

interface PhysicalAccountSectionProps {
  accountId: PhysicalAccountId | undefined;
  tradeProposalId: TradeProposalId;
}

const PhysicalAccountSection: React.FC<PhysicalAccountSectionProps> = (
  props
) => {
  const auth = useAdvisorRequestAuth();
  const physicalAccount = usePhysicalAccount(props.accountId ?? null, auth);
  const isMutualFunds = useIsMutualFunds(props.tradeProposalId);

  return (
    <>
      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Custodian</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {physicalAccount.data?.custodian ?? <Skeleton width={100} />}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Custodian ID</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {physicalAccount.data?.custodianAccountId ?? <Skeleton width={100} />}
        </Text>
      </div>

      <div className={styles.FlexRow}>
        <Text variant="bodyBold">Mutual Funds</Text>
        <Text variant="bodyBold">:&nbsp;</Text>
        <Text variant="body">
          {isMutualFunds === undefined ? (
            <Skeleton width={100} />
          ) : isMutualFunds === null ? (
            UNKNOWN_VALUE
          ) : isMutualFunds ? (
            "Yes"
          ) : (
            "No"
          )}
        </Text>
      </div>
    </>
  );
};
